import { useSession } from './session';
import type { SessionPayload } from './types';

/**
 * Tenant module switches published by the platform (`platform.appearance.modules` in the session's
 * public platform config). The server enforces them (403 MODULE_DISABLED); the web app mirrors them
 * by hiding navigation and showing a "disabled by the platform administrator" screen.
 *
 * Keep MODULE_ROUTES in sync with TENANT_MODULES in apps/api/src/services/platform/appearance.ts.
 */
export const MODULE_ROUTES: ReadonlyArray<{ key: string; paths: string[] }> = [
  { key: 'review_queue', paths: ['/review'] },
  { key: 'library', paths: ['/library'] },
  { key: 'automation', paths: ['/automation'] },
  { key: 'audit_exports', paths: ['/audit'] },
];

export interface PublicModuleState {
  enabled: boolean;
  label: string;
}

type PlatformConfig = SessionPayload['platform'];

let snapshot: PlatformConfig = undefined;

/** Called by usePlatformAppearance so `isModuleEnabled(key)` works outside React components. */
export function setModuleSnapshot(platform: PlatformConfig) {
  snapshot = platform;
}

export function moduleStates(platform: PlatformConfig = snapshot): Record<string, PublicModuleState> {
  const mods = (platform?.['platform.appearance'] as { modules?: unknown } | undefined)?.modules;
  if (!mods || typeof mods !== 'object') return {};
  const out: Record<string, PublicModuleState> = {};
  for (const [k, v] of Object.entries(mods as Record<string, unknown>)) {
    if (v && typeof v === 'object') out[k] = { enabled: (v as { enabled?: unknown }).enabled !== false, label: String((v as { label?: unknown }).label ?? k) };
  }
  return out;
}

/** True unless the platform explicitly disabled the module (unknown keys and missing config = enabled). */
export function isModuleEnabled(key: string, platform: PlatformConfig = snapshot): boolean {
  return moduleStates(platform)[key]?.enabled !== false;
}

/** Operator-chosen label for a module, or `fallback`. */
export function moduleLabel(key: string, fallback: string, platform: PlatformConfig = snapshot): string {
  return moduleStates(platform)[key]?.label || fallback;
}

/** The module owning a web route (e.g. `/library/templates/abc` → `library`), or null. */
export function moduleForPath(pathname: string): string | null {
  for (const m of MODULE_ROUTES) {
    if (m.paths.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return m.key;
  }
  return null;
}

/** Hook form bound to the live session payload. */
export function useModules() {
  const { platform } = useSession();
  return {
    isEnabled: (key: string) => isModuleEnabled(key, platform),
    label: (key: string, fallback: string) => moduleLabel(key, fallback, platform),
    isPathEnabled: (pathname: string) => {
      const key = moduleForPath(pathname);
      return !key || isModuleEnabled(key, platform);
    },
  };
}
